import { isDefined } from "@clipboard-health/util-ts";
import { useContractorAgreementReSigning } from "@src/appV2/Agreements/hooks/useContractorAgreementReSigning";
import { environmentConfig } from "@src/appV2/environment";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PdfViewerProvider } from "@src/appV2/PdfViewer/context";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useUpdateWorkerCache } from "@src/appV2/Worker/useUpdateWorkerCache";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import { PageWrapper } from "../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { useSignICAgreement } from "../../Onboarding/OnboardingFlowContainer/ICAgreementStep/useSignICAgreement";
import { useGetICAgreementToSign } from "../hooks/useGetICAgreementToSign";
import { ICAgreementFormV2 } from "../ICAgreementFormV2";

export function SignNewAgreementPageContainer() {
  const worker = useDefinedWorker();
  const history = useHistory();
  const queryClient = useQueryClient();

  const workerId = worker.userId;

  const { showErrorToast } = useToast();

  const { needsReSigning } = useContractorAgreementReSigning({ worker });

  const { agreementToSign, pdfUrl, agreementsStatus } = useGetICAgreementToSign({ workerId });

  const updateWorkerCache = useUpdateWorkerCache();

  useLogEffect(
    APP_V2_USER_EVENTS.SIGN_AGREEMENT_VIEWED,
    {
      hcpId: worker.userId,
      name: worker.name,
      agreementVersion: agreementToSign?.attributes.version,
      isReSigning: needsReSigning,
    },
    { enabled: isDefined(agreementToSign) }
  );

  useLogEffect(
    APP_V2_APP_EVENTS.CONTRACTOR_AGREEMENT_RE_SIGNING_VIEWED,
    {
      hcpId: worker.userId,
      name: worker.name,
      agreementVersion: agreementToSign?.attributes.version,
    },
    { enabled: needsReSigning && isDefined(agreementToSign) }
  );

  const { mutateAsync: signICAgreement, isLoading: isSigningAgreement } = useSignICAgreement({
    onError: (error) => {
      showErrorToast(
        "Sorry we are experiencing technical problems. Please contact support, or try again later."
      );
      logError(APP_V2_APP_EVENTS.SIGN_NEW_AGREEMENT_FAILURE, {
        error,
        metadata: {
          userId: worker.userId,
        },
      });
    },
  });

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader title="Sign New Agreement" headerVariant="tertiary" />
      <PdfViewerProvider fileName="clipboard_health_contract.pdf" pdfUrl={pdfUrl}>
        <ICAgreementFormV2
          worker={worker}
          agreementToSign={agreementToSign}
          agreementsStatus={agreementsStatus}
          isSigningAgreement={isSigningAgreement}
          onSignAgreement={async ({ agreementToSign }) => {
            if (needsReSigning) {
              logEvent(APP_V2_APP_EVENTS.CONTRACTOR_AGREEMENT_RE_SIGNING_COMPLETED, {
                hcpId: worker.userId,
                name: worker.name,
                agreementVersion: agreementToSign?.attributes.version,
              });
            }

            const { signedContractorAgreements } = await signICAgreement({
              agreementVersion: agreementToSign.attributes.version,
              signature: worker.name,
              workerId: worker.userId,
              forceReSignAgreement: needsReSigning,
            });

            logEvent(APP_V2_USER_EVENTS.SIGN_NEW_AGREEMENT_STARTED, {
              workerId: worker.userId,
              email: worker.email,
              signature: worker.name,
              agreementVersion: agreementToSign.attributes.version,
            });

            updateWorkerCache({ signedContractorAgreements });

            queryClient.removeQueries({
              predicate: (query) =>
                Array.isArray(query.queryKey) &&
                typeof query.queryKey[0] === "string" &&
                query.queryKey[0] === `${environmentConfig.REACT_APP_BASE_API_URL}/agreements`,
            });

            logEvent(APP_V2_APP_EVENTS.SIGN_NEW_AGREEMENT_SUCCESS, {
              agreementVersion: agreementToSign.attributes.version,
            });

            history.push("/");
          }}
        />
      </PdfViewerProvider>
    </PageWrapper>
  );
}
